import React, { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Power,
  Eye,
  Code,
  RefreshCw,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import silaeVariableService from "@/services/silaeVariableService";
import type {
  SilaeVariable,
  SilaeVariableType,
  SilaeVariableCategory,
  CreateSilaeVariableInput,
  UpdateSilaeVariableInput,
  GetSilaeVariablesFilters,
  AdvancedVariableParams,
} from "@/types/silae";

// Define constants for enum values
const VariableType = {
  DATE: "date" as SilaeVariableType,
  INT: "int" as SilaeVariableType,
  STRING: "string" as SilaeVariableType,
  DOUBLE: "double" as SilaeVariableType,
  BOOLEAN: "boolean" as SilaeVariableType,
  SELECTION: "selection" as SilaeVariableType,
  FUNCTION: "function" as SilaeVariableType,
  VARIABLE: "variable" as SilaeVariableType,
} as const;

const VariableCategory = {
  BULLETIN: "bulletin" as SilaeVariableCategory,
  SALARY: "salary" as SilaeVariableCategory,
  EMPLOYEE: "employee" as SilaeVariableCategory,
  MTPART: "mtpart" as SilaeVariableCategory,
  FUNCTIONS: "functions" as SilaeVariableCategory,
  OTHER: "other" as SilaeVariableCategory,
} as const;

export const SilaeVariablesTab: React.FC = () => {
  const [variables, setVariables] = useState<SilaeVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<GetSilaeVariablesFilters>({});

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVariable, setSelectedVariable] =
    useState<SilaeVariable | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);

  // Form state (utilisé pour création et édition)
  const [editForm, setEditForm] = useState<
    CreateSilaeVariableInput | UpdateSilaeVariableInput
  >({});

  // Load variables
  const loadVariables = useCallback(async () => {
    try {
      setLoading(true);
      const data = await silaeVariableService.getSilaeVariables({
        ...filters,
        search: searchTerm || undefined,
      });
      setVariables(data);
    } catch (error) {
      console.error("Error loading SILAE variables:", error);
      toast.error("Erreur lors du chargement des variables SILAE");
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    loadVariables();
  }, [filters, searchTerm, loadVariables]);

  // Handle create/edit variable
  const handleSaveVariable = async () => {
    try {
      if (isCreateMode) {
        await silaeVariableService.createSilaeVariable(
          editForm as CreateSilaeVariableInput,
        );
        toast.success("Variable SILAE créée avec succès");
      } else {
        if (!selectedVariable) return;
        await silaeVariableService.updateSilaeVariable(
          selectedVariable.id,
          editForm as UpdateSilaeVariableInput,
        );
        toast.success("Variable SILAE mise à jour avec succès");
      }
      setEditDialogOpen(false);
      setEditForm({});
      setSelectedVariable(null);
      setIsCreateMode(false);
      loadVariables();
    } catch (error: unknown) {
      console.error(
        `Error ${isCreateMode ? "creating" : "updating"} variable:`,
        error,
      );
      const errorMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data &&
        typeof error.response.data.message === "string"
          ? error.response.data.message
          : `Erreur lors de la ${isCreateMode ? "création" : "mise à jour"}`;
      toast.error(errorMessage);
    }
  };

  // Handle delete variable
  const handleDeleteVariable = async () => {
    if (!selectedVariable) return;

    try {
      await silaeVariableService.deleteSilaeVariable(selectedVariable.id);
      toast.success("Variable SILAE supprimée avec succès");
      setDeleteDialogOpen(false);
      setSelectedVariable(null);
      loadVariables();
    } catch (error: unknown) {
      console.error("Error deleting variable:", error);
      const errorMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data &&
        typeof error.response.data.message === "string"
          ? error.response.data.message
          : "Erreur lors de la suppression";
      toast.error(errorMessage);
    }
  };

  // Handle toggle active
  const handleToggleActive = async (variable: SilaeVariable) => {
    try {
      await silaeVariableService.toggleSilaeVariableActive(variable.id);
      toast.success(
        `Variable ${variable.isActive ? "désactivée" : "activée"} avec succès`,
      );
      loadVariables();
    } catch (error: unknown) {
      console.error("Error toggling variable:", error);
      toast.error("Erreur lors du changement de statut");
    }
  };

  // Open create dialog
  const openCreateDialog = () => {
    setIsCreateMode(true);
    setSelectedVariable(null);
    setEditForm({
      code: "",
      type: VariableType.STRING,
      definition: "",
      category: VariableCategory.OTHER,
      example: "",
      isActive: true,
      isFrequentlyUsed: false,
      label: "",
      unit: "",
      parameters: undefined,
      options: undefined,
    });
    setEditDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (variable: SilaeVariable) => {
    setIsCreateMode(false);
    setSelectedVariable(variable);
    setEditForm({
      code: variable.code,
      type: variable.type,
      definition: variable.definition,
      category: variable.category,
      example: variable.example || "",
      isActive: variable.isActive,
      isFrequentlyUsed: variable.isFrequentlyUsed,
      label: variable.label || "",
      unit: variable.unit || "",
      parameters: variable.parameters || undefined,
      options: variable.options || undefined,
    });
    setEditDialogOpen(true);
  };

  // Open view dialog
  const openViewDialog = (variable: SilaeVariable) => {
    setSelectedVariable(variable);
    setViewDialogOpen(true);
  };

  // Open delete dialog
  const openDeleteDialog = (variable: SilaeVariable) => {
    setSelectedVariable(variable);
    setDeleteDialogOpen(true);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm("");
  };

  // Get type badge color
  const getTypeBadgeColor = (type: SilaeVariableType) => {
    switch (type) {
      case VariableType.DATE:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case VariableType.INT:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case VariableType.DOUBLE:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case VariableType.BOOLEAN:
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case VariableType.SELECTION:
        return "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300";
      case VariableType.FUNCTION:
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case VariableType.VARIABLE:
        return "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  // Get category badge color
  const getCategoryBadgeColor = (category: SilaeVariableCategory) => {
    switch (category) {
      case VariableCategory.BULLETIN:
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300";
      case VariableCategory.SALARY:
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300";
      case VariableCategory.EMPLOYEE:
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case VariableCategory.MTPART:
        return "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300";
      case VariableCategory.FUNCTIONS:
        return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Variables SILAE</h2>
          <p className="text-muted-foreground">
            Gérez les variables et fonctions du langage SILAE
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={loadVariables} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Variable
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher par code ou définition..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={filters.category || "all"}
          onValueChange={(value) =>
            setFilters({
              ...filters,
              category:
                value === "all" ? undefined : (value as SilaeVariableCategory),
            })
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            <SelectItem value={VariableCategory.BULLETIN}>Bulletin</SelectItem>
            <SelectItem value={VariableCategory.SALARY}>Salarié</SelectItem>
            <SelectItem value={VariableCategory.EMPLOYEE}>Emploi</SelectItem>
            <SelectItem value={VariableCategory.MTPART}>Montants</SelectItem>
            <SelectItem value={VariableCategory.FUNCTIONS}>
              Fonctions
            </SelectItem>
            <SelectItem value={VariableCategory.OTHER}>Autres</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.type || "all"}
          onValueChange={(value) =>
            setFilters({
              ...filters,
              type: value === "all" ? undefined : (value as SilaeVariableType),
            })
          }
        >
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            <SelectItem value={VariableType.STRING}>String</SelectItem>
            <SelectItem value={VariableType.INT}>Integer</SelectItem>
            <SelectItem value={VariableType.DOUBLE}>Double</SelectItem>
            <SelectItem value={VariableType.DATE}>Date</SelectItem>
            <SelectItem value={VariableType.BOOLEAN}>Boolean</SelectItem>
            <SelectItem value={VariableType.SELECTION}>Selection</SelectItem>
            <SelectItem value={VariableType.FUNCTION}>Function</SelectItem>
            <SelectItem value={VariableType.VARIABLE}>Variable</SelectItem>
          </SelectContent>
        </Select>
        {(filters.category || filters.type || searchTerm) && (
          <Button variant="outline" size="sm" onClick={clearFilters}>
            <Filter className="h-4 w-4 mr-2" />
            Effacer
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead>Définition</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  Chargement des variables...
                </TableCell>
              </TableRow>
            ) : variables.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  Aucune variable trouvée.
                </TableCell>
              </TableRow>
            ) : (
              variables.map((variable) => (
                <TableRow key={variable.id}>
                  <TableCell className="font-mono font-medium">
                    <div className="flex items-center space-x-2">
                      <Code className="h-4 w-4 text-muted-foreground" />
                      <span>{variable.code}</span>
                      {variable.isFrequentlyUsed && (
                        <Badge variant="secondary" className="text-xs">
                          Fréquent
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getTypeBadgeColor(variable.type)}>
                      {variable.type.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getCategoryBadgeColor(variable.category)}>
                      {variable.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {variable.definition}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={variable.isActive ? "default" : "secondary"}
                    >
                      {variable.isActive ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => openViewDialog(variable)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Voir
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openEditDialog(variable)}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleToggleActive(variable)}
                        >
                          <Power className="mr-2 h-4 w-4" />
                          {variable.isActive ? "Désactiver" : "Activer"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => openDeleteDialog(variable)}
                          className="text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Statistics */}
      <div className="text-sm text-muted-foreground">
        {variables.length} variable{variables.length > 1 ? "s" : ""} au total
      </div>

      {/* Edit/Create Dialog */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            // Réinitialiser l'état quand on ferme le dialog
            setEditForm({});
            setSelectedVariable(null);
            setIsCreateMode(false);
          }
        }}
      >
        <DialogContent className="max-w-6xl w-[95vw] max-h-[80vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>
              {isCreateMode
                ? "Créer une nouvelle variable SILAE"
                : "Modifier la variable SILAE"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 min-w-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="edit-code">Code *</Label>
                <Input
                  id="edit-code"
                  value={editForm.code || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, code: e.target.value })
                  }
                  className="w-full"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="edit-type">Type *</Label>
                <Select
                  value={editForm.type || ""}
                  onValueChange={(value) =>
                    setEditForm({
                      ...editForm,
                      type: value as SilaeVariableType,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VariableType.STRING}>String</SelectItem>
                    <SelectItem value={VariableType.INT}>Integer</SelectItem>
                    <SelectItem value={VariableType.DOUBLE}>Double</SelectItem>
                    <SelectItem value={VariableType.DATE}>Date</SelectItem>
                    <SelectItem value={VariableType.BOOLEAN}>
                      Boolean
                    </SelectItem>
                    <SelectItem value={VariableType.SELECTION}>
                      Selection
                    </SelectItem>
                    <SelectItem value={VariableType.FUNCTION}>
                      Function
                    </SelectItem>
                    <SelectItem value={VariableType.VARIABLE}>
                      Variable
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-category">Catégorie</Label>
              <Select
                value={editForm.category || ""}
                onValueChange={(value) =>
                  setEditForm({
                    ...editForm,
                    category: value as SilaeVariableCategory,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={VariableCategory.BULLETIN}>
                    Variables Bulletin
                  </SelectItem>
                  <SelectItem value={VariableCategory.SALARY}>
                    Variables Salarié
                  </SelectItem>
                  <SelectItem value={VariableCategory.EMPLOYEE}>
                    Variables Emploi
                  </SelectItem>
                  <SelectItem value={VariableCategory.MTPART}>
                    Montants Particuliers
                  </SelectItem>
                  <SelectItem value={VariableCategory.FUNCTIONS}>
                    Fonctions
                  </SelectItem>
                  <SelectItem value={VariableCategory.OTHER}>Autres</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-definition">Définition *</Label>
              <Textarea
                id="edit-definition"
                value={editForm.definition || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, definition: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-example">Exemple d'utilisation</Label>
              <Input
                id="edit-example"
                value={editForm.example || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, example: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="edit-label">Libellé</Label>
                <Input
                  id="edit-label"
                  placeholder="Ex: Date du bulletin"
                  value={editForm.label || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, label: e.target.value })
                  }
                  className="w-full"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="edit-unit">Unité</Label>
                <Input
                  id="edit-unit"
                  placeholder="Ex: jours, €, %"
                  value={editForm.unit || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, unit: e.target.value })
                  }
                  className="w-full"
                />
              </div>
            </div>

            {/* Paramètres pour les fonctions */}
            {editForm.type === VariableType.FUNCTION && (
              <div className="space-y-4 border rounded-lg p-4 min-w-0">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">
                    Paramètres de la fonction
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const nextIndex = (editForm.parameters?.length ?? 0) + 1;
                      const newParam: AdvancedVariableParams = {
                        id: `param${nextIndex}`,
                        isRequired: false,
                        label: "",
                      };
                      setEditForm({
                        ...editForm,
                        parameters: [...(editForm.parameters || []), newParam],
                      });
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un paramètre
                  </Button>
                </div>
                {editForm.parameters && editForm.parameters.length > 0 && (
                  <div className="space-y-4">
                    {editForm.parameters.map((param, index) => (
                      <div
                        key={param.id || index}
                        className="border rounded-lg p-4 space-y-3 bg-muted/50 min-w-0"
                      >
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">
                            Paramètre {index + 1}
                          </Label>
                          <div className="flex items-center gap-2">
                            {index > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newParams = [...editForm.parameters!];
                                  const temp = newParams[index];
                                  newParams[index] = newParams[index - 1];
                                  newParams[index - 1] = temp;
                                  setEditForm({
                                    ...editForm,
                                    parameters: newParams,
                                  });
                                }}
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                            )}
                            {index < editForm.parameters!.length - 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newParams = [...editForm.parameters!];
                                  const temp = newParams[index];
                                  newParams[index] = newParams[index + 1];
                                  newParams[index + 1] = temp;
                                  setEditForm({
                                    ...editForm,
                                    parameters: newParams,
                                  });
                                }}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditForm({
                                  ...editForm,
                                  parameters: editForm.parameters!.filter(
                                    (_, i) => i !== index,
                                  ),
                                });
                              }}
                            >
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2 min-w-0">
                            <Label htmlFor={`param-id-${index}`}>ID *</Label>
                            <Input
                              id={`param-id-${index}`}
                              value={param.id || ""}
                              onChange={(e) => {
                                const newParams = [...editForm.parameters!];
                                newParams[index] = {
                                  ...newParams[index],
                                  id: e.target.value,
                                };
                                setEditForm({
                                  ...editForm,
                                  parameters: newParams,
                                });
                              }}
                              className="w-full"
                            />
                          </div>
                          <div className="space-y-2 min-w-0">
                            <Label htmlFor={`param-label-${index}`}>
                              Libellé
                            </Label>
                            <Input
                              id={`param-label-${index}`}
                              value={param.label || ""}
                              onChange={(e) => {
                                const newParams = [...editForm.parameters!];
                                newParams[index] = {
                                  ...newParams[index],
                                  label: e.target.value,
                                };
                                setEditForm({
                                  ...editForm,
                                  parameters: newParams,
                                });
                              }}
                              className="w-full"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2 min-w-0">
                            <Label htmlFor={`param-type-${index}`}>Type</Label>
                            <Select
                              value={param.type || ""}
                              onValueChange={(value) => {
                                const newParams = [...editForm.parameters!];
                                const updates: Partial<AdvancedVariableParams> =
                                  {
                                    type: value,
                                  };
                                // Initialiser les options si on change vers "selection"
                                if (
                                  value === "selection" &&
                                  !newParams[index].options
                                ) {
                                  updates.options = [];
                                }
                                // Nettoyer les options si on change vers un autre type
                                if (
                                  value !== "selection" &&
                                  newParams[index].options
                                ) {
                                  updates.options = undefined;
                                }
                                newParams[index] = {
                                  ...newParams[index],
                                  ...updates,
                                };
                                setEditForm({
                                  ...editForm,
                                  parameters: newParams,
                                });
                              }}
                            >
                              <SelectTrigger
                                className="w-full"
                                id={`param-type-${index}`}
                              >
                                <SelectValue placeholder="Sélectionner un type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="selection">
                                  selection
                                </SelectItem>
                                <SelectItem value="string">string</SelectItem>
                                <SelectItem value="dynamicDate">
                                  date (constructeur dynamique)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2 min-w-0">
                            <Label htmlFor={`param-placeholder-${index}`}>
                              Placeholder
                            </Label>
                            <Input
                              id={`param-placeholder-${index}`}
                              value={param.placeholder || ""}
                              onChange={(e) => {
                                const newParams = [...editForm.parameters!];
                                newParams[index] = {
                                  ...newParams[index],
                                  placeholder: e.target.value,
                                };
                                setEditForm({
                                  ...editForm,
                                  parameters: newParams,
                                });
                              }}
                              className="w-full"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`param-helper-${index}`}>
                            Aide contextuelle
                          </Label>
                          <Textarea
                            id={`param-helper-${index}`}
                            rows={2}
                            value={param.helper || ""}
                            onChange={(e) => {
                              const newParams = [...editForm.parameters!];
                              newParams[index] = {
                                ...newParams[index],
                                helper: e.target.value,
                              };
                              setEditForm({
                                ...editForm,
                                parameters: newParams,
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`param-note-${index}`}>Note</Label>
                          <Textarea
                            id={`param-note-${index}`}
                            rows={2}
                            value={param.note || ""}
                            onChange={(e) => {
                              const newParams = [...editForm.parameters!];
                              newParams[index] = {
                                ...newParams[index],
                                note: e.target.value,
                              };
                              setEditForm({
                                ...editForm,
                                parameters: newParams,
                              });
                            }}
                          />
                        </div>

                        {/* Options pour les paramètres de type selection */}
                        {param.type === "selection" && (
                          <div className="space-y-3 border-t pt-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-semibold">
                                Options du paramètre
                              </Label>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newParams = [...editForm.parameters!];
                                  const currentOptions =
                                    newParams[index].options || [];
                                  newParams[index] = {
                                    ...newParams[index],
                                    options: [
                                      ...currentOptions,
                                      { code: "", label: "" },
                                    ],
                                  };
                                  setEditForm({
                                    ...editForm,
                                    parameters: newParams,
                                  });
                                }}
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Ajouter une option
                              </Button>
                            </div>
                            {param.options && param.options.length > 0 && (
                              <div className="space-y-2">
                                {param.options.map((option, optionIndex) => (
                                  <div
                                    key={optionIndex}
                                    className="flex items-center gap-2 border rounded-lg p-2 bg-muted/30 min-w-0"
                                  >
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 min-w-0">
                                      <div className="space-y-1 min-w-0">
                                        <Label className="text-xs">Code</Label>
                                        <Input
                                          value={option.code || ""}
                                          onChange={(e) => {
                                            const newParams = [
                                              ...editForm.parameters!,
                                            ];
                                            const currentOptions =
                                              newParams[index].options || [];
                                            currentOptions[optionIndex] = {
                                              ...currentOptions[optionIndex],
                                              code: e.target.value,
                                            };
                                            newParams[index] = {
                                              ...newParams[index],
                                              options: currentOptions,
                                            };
                                            setEditForm({
                                              ...editForm,
                                              parameters: newParams,
                                            });
                                          }}
                                          placeholder="Code de l'option"
                                          className="w-full"
                                        />
                                      </div>
                                      <div className="space-y-1 min-w-0">
                                        <Label className="text-xs">
                                          Libellé
                                        </Label>
                                        <Input
                                          value={option.label || ""}
                                          onChange={(e) => {
                                            const newParams = [
                                              ...editForm.parameters!,
                                            ];
                                            const currentOptions =
                                              newParams[index].options || [];
                                            currentOptions[optionIndex] = {
                                              ...currentOptions[optionIndex],
                                              label: e.target.value,
                                            };
                                            newParams[index] = {
                                              ...newParams[index],
                                              options: currentOptions,
                                            };
                                            setEditForm({
                                              ...editForm,
                                              parameters: newParams,
                                            });
                                          }}
                                          placeholder="Libellé de l'option"
                                          className="w-full"
                                        />
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const newParams = [
                                          ...editForm.parameters!,
                                        ];
                                        const currentOptions =
                                          newParams[index].options || [];
                                        newParams[index] = {
                                          ...newParams[index],
                                          options: currentOptions.filter(
                                            (_, i) => i !== optionIndex,
                                          ),
                                        };
                                        setEditForm({
                                          ...editForm,
                                          parameters: newParams,
                                        });
                                      }}
                                    >
                                      <X className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Switches de configuration */}
                        <div className="flex flex-wrap items-center gap-4 min-w-0 border-t pt-3">
                          {/* Switch Requis — indépendant */}
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`param-required-${index}`}
                              checked={param.isRequired || false}
                              onCheckedChange={(checked) => {
                                const newParams = [...editForm.parameters!];
                                newParams[index] = {
                                  ...newParams[index],
                                  isRequired: checked,
                                };
                                setEditForm({
                                  ...editForm,
                                  parameters: newParams,
                                });
                              }}
                            />
                            <Label htmlFor={`param-required-${index}`}>
                              Requis
                            </Label>
                          </div>

                          {/* Toggle group exclusif : Fixé / Variable de sortie (désactivable) */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground">
                              Valeur :
                            </Label>
                            <div className="flex h-8 rounded-md border border-border overflow-hidden text-xs font-medium">
                              {(
                                [
                                  { value: "fixed", label: "Fixée" },
                                  { value: "output", label: "Sortie" },
                                ] as const
                              ).map(({ value, label }) => {
                                const active =
                                  value === "fixed"
                                    ? !!param.isFixed
                                    : !!param.isOutputParam;
                                return (
                                  <button
                                    key={value}
                                    type="button"
                                    onClick={() => {
                                      const newParams = [
                                        ...editForm.parameters!,
                                      ];
                                      // Si déjà actif → désactiver (toggle off)
                                      const turningOff = active;
                                      newParams[index] = {
                                        ...newParams[index],
                                        isFixed:
                                          !turningOff && value === "fixed",
                                        isOutputParam:
                                          !turningOff && value === "output",
                                        ...(value === "fixed" || turningOff
                                          ? {
                                              fixedValue:
                                                turningOff && value === "fixed"
                                                  ? undefined
                                                  : newParams[index].fixedValue,
                                            }
                                          : {}),
                                        ...(value === "output" || turningOff
                                          ? {
                                              outputVarName:
                                                turningOff && value === "output"
                                                  ? undefined
                                                  : newParams[index]
                                                      .outputVarName,
                                            }
                                          : {}),
                                        // Nettoyage de l'autre option
                                        ...(value === "fixed" && !turningOff
                                          ? { outputVarName: undefined }
                                          : {}),
                                        ...(value === "output" && !turningOff
                                          ? { fixedValue: undefined }
                                          : {}),
                                      };
                                      setEditForm({
                                        ...editForm,
                                        parameters: newParams,
                                      });
                                    }}
                                    className={cn(
                                      "px-3 h-full transition-colors border-r last:border-r-0 border-border",
                                      active
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
                                    )}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {param.type === "selection" && (
                            <div className="flex items-center space-x-2">
                              <Switch
                                id={`param-include-bulletin-${index}`}
                                checked={param.includeBulletinCourant || false}
                                onCheckedChange={(checked) => {
                                  const newParams = [...editForm.parameters!];
                                  newParams[index] = {
                                    ...newParams[index],
                                    includeBulletinCourant: checked,
                                  };
                                  setEditForm({
                                    ...editForm,
                                    parameters: newParams,
                                  });
                                }}
                              />
                              <Label
                                htmlFor={`param-include-bulletin-${index}`}
                              >
                                Inclure bulletin courant
                              </Label>
                            </div>
                          )}
                        </div>

                        {/* Champs conditionnels pour les valeurs */}
                        {param.isFixed && (
                          <div className="space-y-2 min-w-0">
                            <Label htmlFor={`param-fixed-value-${index}`}>
                              Valeur fixe *
                            </Label>
                            <Input
                              id={`param-fixed-value-${index}`}
                              value={param.fixedValue || ""}
                              onChange={(e) => {
                                const newParams = [...editForm.parameters!];
                                newParams[index] = {
                                  ...newParams[index],
                                  fixedValue: e.target.value,
                                };
                                setEditForm({
                                  ...editForm,
                                  parameters: newParams,
                                });
                              }}
                              className="w-full"
                              placeholder="Valeur fixe du paramètre"
                            />
                          </div>
                        )}

                        {param.isOutputParam && (
                          <div className="space-y-2 min-w-0">
                            <Label htmlFor={`param-output-varname-${index}`}>
                              Nom de la variable de sortie *
                            </Label>
                            <Input
                              id={`param-output-varname-${index}`}
                              value={param.outputVarName || ""}
                              onChange={(e) => {
                                const newParams = [...editForm.parameters!];
                                newParams[index] = {
                                  ...newParams[index],
                                  outputVarName: e.target.value,
                                };
                                setEditForm({
                                  ...editForm,
                                  parameters: newParams,
                                });
                              }}
                              className="w-full"
                              placeholder="Ex: bs, ts, rp…"
                            />
                            <p className="text-xs text-muted-foreground">
                              Ce nom sera déclaré avant l'appel de la fonction
                              (ex:{" "}
                              <code>{param.outputVarName || "bs"} = 0</code>).
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Options pour les types selection */}
            {(editForm.type === VariableType.SELECTION ||
              editForm.type === VariableType.VARIABLE) && (
              <div className="space-y-4 border rounded-lg p-4 min-w-0">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">
                    Options disponibles
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newOption = {
                        code: "",
                        label: "",
                      };
                      setEditForm({
                        ...editForm,
                        options: [...(editForm.options || []), newOption],
                      });
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une option
                  </Button>
                </div>
                {editForm.options && editForm.options.length > 0 && (
                  <div className="space-y-3">
                    {editForm.options.map((option, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 border rounded-lg p-3 bg-muted/50 min-w-0"
                      >
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 min-w-0">
                          <div className="space-y-1 min-w-0">
                            <Label className="text-xs">Code</Label>
                            <Input
                              value={option.code || ""}
                              onChange={(e) => {
                                const newOptions = [...editForm.options!];
                                newOptions[index] = {
                                  ...newOptions[index],
                                  code: e.target.value,
                                };
                                setEditForm({
                                  ...editForm,
                                  options: newOptions,
                                });
                              }}
                              placeholder="Code de l'option"
                              className="w-full"
                            />
                          </div>
                          <div className="space-y-1 min-w-0">
                            <Label className="text-xs">Libellé</Label>
                            <Input
                              value={option.label || ""}
                              onChange={(e) => {
                                const newOptions = [...editForm.options!];
                                newOptions[index] = {
                                  ...newOptions[index],
                                  label: e.target.value,
                                };
                                setEditForm({
                                  ...editForm,
                                  options: newOptions,
                                });
                              }}
                              placeholder="Libellé de l'option"
                              className="w-full"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditForm({
                              ...editForm,
                              options: editForm.options!.filter(
                                (_, i) => i !== index,
                              ),
                            });
                          }}
                        >
                          <X className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-active"
                  checked={editForm.isActive ?? false}
                  onCheckedChange={(checked) =>
                    setEditForm({ ...editForm, isActive: checked })
                  }
                />
                <Label htmlFor="edit-active">Active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-frequent"
                  checked={editForm.isFrequentlyUsed ?? false}
                  onCheckedChange={(checked) =>
                    setEditForm({ ...editForm, isFrequentlyUsed: checked })
                  }
                />
                <Label htmlFor="edit-frequent">Fréquemment utilisée</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false);
                setEditForm({});
                setSelectedVariable(null);
                setIsCreateMode(false);
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveVariable}
              disabled={!editForm.code || !editForm.definition}
            >
              {isCreateMode ? "Créer" : "Mettre à jour"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails de la variable SILAE</DialogTitle>
          </DialogHeader>
          {selectedVariable && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Code</Label>
                  <p className="font-mono text-sm break-all">
                    {selectedVariable.code || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Type</Label>
                  <Badge
                    className={getTypeBadgeColor(
                      selectedVariable.type || "string",
                    )}
                  >
                    {(selectedVariable.type || "string").toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Catégorie</Label>
                <br />
                <Badge
                  className={getCategoryBadgeColor(
                    selectedVariable.category || "other",
                  )}
                >
                  {selectedVariable.categoryLabel ||
                    selectedVariable.category ||
                    "Autre"}
                </Badge>
              </div>
              <div>
                <Label className="text-sm font-medium">Définition</Label>
                <p className="text-sm wrap-break-word">
                  {selectedVariable.definition || "Aucune définition"}
                </p>
              </div>
              {selectedVariable.example && (
                <div>
                  <Label className="text-sm font-medium">Exemple</Label>
                  <p className="text-sm font-mono bg-muted p-2 rounded break-all">
                    {selectedVariable.example}
                  </p>
                </div>
              )}
              {(selectedVariable.label || selectedVariable.unit) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedVariable.label && (
                    <div>
                      <Label className="text-sm font-medium">Libellé</Label>
                      <p className="text-sm wrap-break-word ">
                        {selectedVariable.label}
                      </p>
                    </div>
                  )}
                  {selectedVariable.unit && (
                    <div>
                      <Label className="text-sm font-medium">Unité</Label>
                      <p className="text-sm font-mono">
                        {selectedVariable.unit}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {selectedVariable.parameters &&
                selectedVariable.parameters.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Paramètres</Label>
                    <div className="mt-2 space-y-2">
                      {selectedVariable.parameters.map((param, index) => (
                        <div
                          key={index}
                          className="text-sm bg-muted p-2 rounded"
                        >
                          <p className="font-medium">
                            {param.label || param.id}
                          </p>
                          {param.type && (
                            <p className="text-xs text-muted-foreground">
                              Type: {param.type}
                            </p>
                          )}
                          {param.fixedValue && (
                            <p className="text-xs text-muted-foreground">
                              Fixé à : {param.fixedValue}
                            </p>
                          )}
                          {param.isRequired && (
                            <Badge variant="outline" className="text-xs">
                              Requis
                            </Badge>
                          )}
                          {param.isFixed && (
                            <Badge variant="outline" className="text-xs">
                              Fixé
                            </Badge>
                          )}
                          {param.type === "selection" &&
                            param.options &&
                            param.options.length > 0 && (
                              <div className="mt-2 space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">
                                  Options :
                                </p>
                                {param.options.map((option, optIndex) => (
                                  <div
                                    key={optIndex}
                                    className="text-xs ml-2 text-muted-foreground"
                                  >
                                    <span className="font-mono">
                                      {option.code}
                                    </span>
                                    {option.label && (
                                      <span className="ml-2">
                                        - {option.label}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              {selectedVariable.options &&
                selectedVariable.options.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Options</Label>
                    <div className="mt-2 space-y-1">
                      {selectedVariable.options.map((option, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-mono">{option.code}</span>
                          {option.label && (
                            <span className="text-muted-foreground ml-2">
                              - {option.label}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Label className="text-sm font-medium">Statut:</Label>
                  <Badge
                    variant={
                      selectedVariable.isActive ? "default" : "secondary"
                    }
                  >
                    {selectedVariable.isActive ? "Actif" : "Inactif"}
                  </Badge>
                </div>
                {selectedVariable.isFrequentlyUsed && (
                  <Badge variant="secondary">Fréquemment utilisée</Badge>
                )}
              </div>
              {selectedVariable.createdAt && selectedVariable.updatedAt && (
                <div className="text-xs text-muted-foreground">
                  Créée le{" "}
                  {(() => {
                    try {
                      return new Date(
                        selectedVariable.createdAt,
                      ).toLocaleDateString("fr-FR");
                    } catch {
                      return "Date invalide";
                    }
                  })()}{" "}
                  • Modifiée le{" "}
                  {(() => {
                    try {
                      return new Date(
                        selectedVariable.updatedAt,
                      ).toLocaleDateString("fr-FR");
                    } catch {
                      return "Date invalide";
                    }
                  })()}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la variable SILAE</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer la variable{" "}
              <span className="font-mono font-semibold">
                {selectedVariable?.code}
              </span>{" "}
              ? Cette action est irréversible.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button onClick={handleDeleteVariable} variant="destructive">
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
